import type { UpdateTeamDTO } from "@nlc/shared-types";

import { useNavigate, useParams } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/button";
import { ConfirmationModal } from "@/components/confirmation-modal";
import { FullPageLoader } from "@/components/fullpage-loader";
import { toast } from "@/components/toast/toast-queue";
import { FeaturePageLayout } from "@/features/shared/layout/FeaturePageLayout";
import { useDeleteTeam } from "@/features/teams/shared/hooks/useDeleteTeam";
import { useGetTeam } from "@/features/teams/shared/hooks/useGetTeam";
import { useUpdateTeam } from "@/features/teams/shared/hooks/useUpdateTeam.ts";

import { TeamForm } from "./components/TeamForm";

export function EditTeamPage() {
  const navigate = useNavigate();
  const { mutate: updateTeam } = useUpdateTeam();
  const { isPending: isDeleting, mutate: deleteTeam } = useDeleteTeam();
  const { id } = useParams({
    from: "/_auth/_app-shell/teams/$id/edit",
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data: team, isLoading } = useGetTeam(id);

  const handleSubmit = (data: UpdateTeamDTO) => {
    updateTeam(
      { data, id: Number(id) },
      {
        onError: (error) => {
          console.error("Failed to update team", error);
          toast.add(
            {
              description: error.message || "Failed to update team.",
              title: "Error",
              type: "error",
            },
            { timeout: 5000 },
          );
        },
        onSuccess: () => {
          toast.add(
            {
              description: "The team has been successfully updated.",
              title: "Team Updated",
              type: "success",
            },
            { timeout: 5000 },
          );
          navigate({ to: "/teams" });
        },
      },
    );
  };

  const handleDelete = () => {
    deleteTeam(Number(id), {
      onError: (error) => {
        console.error("Failed to delete team", error);
        toast.add(
          {
            description: error.message || "Failed to delete team.",
            title: "Error",
            type: "error",
          },
          { timeout: 5000 },
        );
      },
      onSuccess: () => {
        toast.add(
          {
            description: "The team has been successfully deleted.",
            title: "Team Deleted",
            type: "success",
          },
          { timeout: 5000 },
        );
        setIsDeleteModalOpen(false);
        navigate({ to: "/teams" });
      },
    });
  };

  if (isLoading) {
    return <FullPageLoader />;
  }

  return (
    <div className="p-xl">
      {team ? (
        <FeaturePageLayout
          headerContent={
            <Button
              className="ml-auto"
              color="destructive"
              onPress={() => setIsDeleteModalOpen(true)}
              size="lg"
              variant="tertiary"
            >
              Delete Team
            </Button>
          }
          title="Edit Team"
        >
          <TeamForm
            initialData={{ ...team, teamLeaderId: team?.teamLeaderId }}
            isEditing={true}
            onCancel={() => navigate({ to: "/teams" })}
            onSubmit={handleSubmit}
            teamId={Number(id)}
          />
        </FeaturePageLayout>
      ) : (
        <FeaturePageLayout title="Edit Team">
          <div className="p-6">Team not found</div>
        </FeaturePageLayout>
      )}
      <ConfirmationModal
        confirmButtonText={isDeleting ? "Deleting..." : "Delete"}
        icon={<Trash2 className="text-featured-icon-light-fg-error" />}
        isConfirmLoading={isDeleting}
        isOpen={isDeleteModalOpen}
        onConfirm={handleDelete}
        onOpenChange={setIsDeleteModalOpen}
        text="This team will be deleted permanently and cannot be retrieved. All associated accounts will be disassociated with the team. Are you sure you want to delete?"
        title="Delete team"
      />
    </div>
  );
}
